import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../constants";
import { isAxiosError } from "axios";
import { isDeadLinkError, probeLinkSession } from "../services/api";
import { useSessionStore } from "../store/useSessionStore";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";
import { RequestStatus } from "../utils/commons";
import {
  expiryRearmAfterProbe,
  linkExpiryDelayMs,
} from "../utils/linkExpiryWatch";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";

const DEAD_LINK_PATHS = [
  APP_ROUTES.SESSION_EXPIRED_PAGE,
  APP_ROUTES.ERROR_PAGE,
  APP_ROUTES.MAINTENANCE_PAGE,
] as const;

/**
 * Foreground link deadline. The local clock only schedules a GET session.
 * The server decides. Network and 5xx stay on the current screen and retry
 * when the browser comes back online. This probe does not open the offline overlay.
 */
export function useLinkExpiryWatch() {
  const location = useLocation();
  const status = useSessionStore((s) => s.session.status);
  const linkId = useSessionStore((s) => s.session.data?.linkId ?? null);
  const expiresAt = useSessionStore((s) => s.session.data?.expiresAt ?? null);

  useEffect(() => {
    if (status !== RequestStatus.LOADED) return;
    if (!linkId || !expiresAt) return;
    if ((DEAD_LINK_PATHS as readonly string[]).includes(location.pathname)) {
      return;
    }
    const urlLinkId = resolveUrlLinkId();
    if (urlLinkId && urlLinkId !== linkId) return;

    const initialDelay = linkExpiryDelayMs(expiresAt, Date.now());
    if (initialDelay == null) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let onlineHandler: (() => void) | undefined;
    let staleRetryUsed = false;

    const clearOnline = () => {
      if (!onlineHandler) return;
      window.removeEventListener("online", onlineHandler);
      onlineHandler = undefined;
    };

    const arm = (delayMs: number) => {
      if (timer != null) window.clearTimeout(timer);
      timer = setTimeout(() => {
        void runProbe();
      }, delayMs);
    };

    const runProbe = async () => {
      if (cancelled) return;
      try {
        const result = await probeLinkSession(linkId);
        if (cancelled) return;
        const current = useSessionStore.getState().session.data;
        if (!current || current.linkId !== linkId) return;

        const decision = expiryRearmAfterProbe(
          result.expiresAt,
          Date.now(),
          staleRetryUsed,
        );
        if (decision.kind === "future" && result.expiresAt) {
          staleRetryUsed = false;
          if (current.expiresAt !== result.expiresAt) {
            useSessionStore.getState().updateSession({
              ...current,
              expiresAt: result.expiresAt,
            });
          }
          arm(decision.delayMs);
          return;
        }
        if (decision.kind === "once") {
          staleRetryUsed = true;
          arm(decision.delayMs);
        }
      } catch (error: unknown) {
        if (cancelled || isDeadLinkError(error)) return;
        if (!isAxiosNetworkFailure(error) && !isServerFailure(error)) return;
        clearOnline();
        onlineHandler = () => {
          clearOnline();
          void runProbe();
        };
        window.addEventListener("online", onlineHandler);
      }
    };

    arm(initialDelay);

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      clearOnline();
    };
  }, [status, linkId, expiresAt, location.pathname]);
}

const isServerFailure = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const statusCode = error.response?.status;
  return statusCode != null && statusCode >= 500;
};
